import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { socketService } from 'services/socket.service'
import type { InvalidationRule } from './types'

/** Default connect strategy: platform-user JWT via `auth: { token }`. */
const connectWithJwt = (token: string) => socketService.connect(token)

/**
 * Core Socket.IO -> React Query bridge. Given an auth token and a set of
 * invalidation rules, it:
 *   1. Runs each rule's optional optimistic `updater` (surgical cache patch)
 *   2. Invalidates the rule's mapped query keys (triggers background refetches)
 *   3. Optionally debounces high-frequency events
 * and connects the shared socket once a token is available.
 *
 * This is principal-agnostic: provider/operation pass the platform-user JWT
 * (via `useAuth`), the customer portal passes its own session/customer token.
 *
 * `rules` and `connect` MUST be stable references (module-level constants or
 * memoized). Passing a fresh array/function each render would re-run the effect
 * and thrash the socket listeners.
 *
 * `connect` lets each surface present its own credential shape: the default
 * uses the platform-user JWT (`auth: { token }`); the customer portal passes a
 * session-scoped connector (`socketService.connectWithAuth(id, { sessionId })`).
 */
export function useSocketQueryBridge(
  token: string | null | undefined,
  rules: InvalidationRule[],
  connect: (token: string) => void = connectWithJwt,
): void {
  const queryClient = useQueryClient()
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map())

  useEffect(() => {
    if (!token) return

    const timers = debounceTimers.current

    const cleanups = rules.map(rule => {
      const handler = (payload: unknown) => {
        rule.updater?.(queryClient, payload)

        const doInvalidate = () => {
          for (const key of rule.invalidate) {
            queryClient.invalidateQueries({ queryKey: [...key] })
          }
        }

        if (rule.debounceMs) {
          const existing = timers.get(rule.event)
          if (existing) clearTimeout(existing)
          timers.set(
            rule.event,
            setTimeout(() => {
              timers.delete(rule.event)
              doInvalidate()
            }, rule.debounceMs),
          )
        } else {
          doInvalidate()
        }
      }

      return socketService.on(rule.event, handler)
    })

    // Refetch-on-(re)connect. A Socket.IO room never replays events emitted
    // before this client joined it: any event fired between the initial data
    // fetch and the socket completing its handshake — or while the socket was
    // down before a reconnect — is lost, leaving the cache silently stale.
    // On every disconnected -> connected transition, invalidate the union of
    // the rules' keys so the mounted queries refetch and recover whatever was
    // missed during that window.
    const uniqueKeys = dedupeKeys(rules)
    let wasConnected = socketService.isConnected()
    const connectionCleanup = socketService.onConnectionStatus(status => {
      if (status.connected && !wasConnected) {
        for (const key of uniqueKeys) {
          queryClient.invalidateQueries({ queryKey: [...key] })
        }
      }
      wasConnected = status.connected
    })

    connect(token)

    return () => {
      cleanups.forEach(cleanup => cleanup())
      connectionCleanup()
      timers.forEach(timer => clearTimeout(timer))
      timers.clear()
    }
  }, [token, queryClient, rules, connect])
}

/** Flatten every rule's invalidation keys into a de-duplicated list. */
function dedupeKeys(rules: InvalidationRule[]): readonly (readonly unknown[])[] {
  const seen = new Map<string, readonly unknown[]>()
  for (const rule of rules) {
    for (const key of rule.invalidate) {
      seen.set(JSON.stringify(key), key)
    }
  }
  return Array.from(seen.values())
}
